import { Command, CommandItem } from "@comps/ui/command";
import { Label } from "@comps/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { TreeDataItem, TreeView } from "@comps/ui/tree-view";
import { PieChartData, PieChartWidgetType, Widget } from "@dto/Dashboard";
import Field, { AggregationType, validAggregationByType, validNumericAggregationByType } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { Filter, Path } from "@dto/records";
import { ASC, DESC, NONE, SortType } from "@dto/views";
import { fieldToPath, pathKey, pathKeyToPath } from "@utils/model";
import { buildFieldTree, buildPathTree, getTreePathValue } from "@utils/tree";
import { TW_DashboardCardFieldArrow, TW_DashboardCardFieldSelected, TW_DashboardCardFieldText, TW_DashboardCardTreeView, TW_DashboardPathSelector, TW_DashboardSection_Arrow, TW_DashboardSection_Label, TW_DashboardSection_ScrollArea, TW_SharedObjectsCollapsibleTrigger, TW_WidgetSectionLabel, TW_WidgetSpecificSection } from "@utils/twClasses";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";
import { WidgetCardConfigProps } from "./WidgetConfig";
import { getDefaultAggregation } from "@utils/types";
import { Separator } from "@comps/ui/separator";
import WidgetFilterEditor from "./WidgetFilterEditor";
import { ScrollArea } from "@comps/ui/scroll";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@comps/ui/collapsible";



export function PieChartCardConfig({ initialWidget, onConfigChange }: WidgetCardConfigProps) {

    const objectInfos = useModelStore(state => state.objectInfos);
    const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
    const workspaceByKeys = useModelStore(state => state.workspaceByKeys);
    const [selectedObject, setSelectedObject] = useState<ObjectInfos | undefined>(undefined);
    const [selectedField, setSelectedField] = useState<Field | undefined>(undefined);
    const [selectedFieldAggregation, setSelectedFieldAggregation] = useState<AggregationType | undefined>(undefined);
    const [selectedCategoryPath, setSelectedCategoryPath] = useState<Path | undefined>(undefined);
    const [selectedCategoryName, setSelectedCategoryName] = useState<string | undefined>(undefined);
    const [selectedCategoryAggregation, setSelectedCategoryAggregation] = useState<AggregationType | undefined>(undefined);
    const [selectedCategorySortType, setSelectedCategorySortType] = useState<SortType>(NONE);
    const [selectedFilter, setSelectedFilter] = useState<Filter | null>(null);
    const [fieldAggregationByPathKeys, setFieldAggregationByPathKeys] = useState<Map<string, AggregationType | undefined>>(new Map<string, AggregationType | undefined>());
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const objectByKeys = useModelStore(state => state.objectByKeys);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const wsColor = useModelStore(state => state.wsColor);
    const { t } = useTranslation();
    const [isSharedObjectsOpen, setIsSharedObjectsOpen] = useState(false);


    useEffect(() => {
        if (initialWidget) {
            if (initialWidget?.data) {
                const data = initialWidget.data as PieChartData;
                if (data.valuePath) {
                    const path = pathKeyToPath(data.valuePath, relationByKeys);
                    if (path) {
                        const field = fieldByKeys?.get(path.field)
                        setSelectedField(field);
                        setSelectedObject(objectByKeys?.get(path.object));
                        setSelectedFieldAggregation(data.valueAggregation ?? undefined);
                    }
                }
                if (data.categoryPath) {
                    const path = pathKeyToPath(data.categoryPath, relationByKeys);
                    if (path) {
                        setSelectedCategoryPath(path);
                        const categoryObject = objectByKeys?.get(path.object);
                        const categoryField = categoryObject?.fields?.find(field => field._key === path.field);
                        setSelectedCategoryName(categoryObject?.object.name + " > " + categoryField?.name);
                        setSelectedCategoryAggregation(data.categoryAggregation ?? undefined);
                        setSelectedCategorySortType(data.categorySortType ?? undefined);
                    }
                }
                if (data.filter) {
                    setSelectedFilter(data.filter);
                }
            }
        }
    }, [initialWidget]);

    function updateFieldAggregationByPathKeys(fieldAggregation: AggregationType | undefined, categoryAggregation: AggregationType | undefined) {
        const newFieldAggregationByPathKeys = new Map<string, AggregationType | undefined>();

        if (fieldAggregation && selectedField?._key) {
            const valuePath = (selectedObject?.object?._key && selectedField) ? fieldToPath(selectedObject.object._key, relationByKeys, fieldByKeys, selectedField) : null;
            const valuePathKey = valuePath ? pathKey(valuePath) : null;
            if (valuePathKey) {
                newFieldAggregationByPathKeys.set(valuePathKey, fieldAggregation);
            }
        }
        if (categoryAggregation && selectedCategoryPath) {
            const categoryPathKey = selectedCategoryPath ? pathKey(selectedCategoryPath) : null;
            if (categoryPathKey) {
                newFieldAggregationByPathKeys.set(categoryPathKey, categoryAggregation);
            }
        }
        setFieldAggregationByPathKeys(newFieldAggregationByPathKeys);
    }

    function getNewWidget(
        valueField: Field | undefined,
        valueAggregation: AggregationType | undefined,
        categoryPath: Path | undefined,
        categoryAggregation: AggregationType | undefined,
        categorySortType: SortType | undefined,
        filter: Filter | null
    ): Widget | undefined {
        if (!valueField || !valueAggregation) {
            return undefined;
        }
        const valuePath = (selectedObject?.object?._key && valueField) ? fieldToPath(selectedObject.object._key, relationByKeys, fieldByKeys, valueField) : null;
        const valuePathKey = valuePath ? pathKey(valuePath) : null;
        const categoryPathKey = categoryPath ? pathKey(categoryPath) : null;
        const newWidget: Widget = {
            type: PieChartWidgetType,
            title: initialWidget?.title || "Pie chart", // Overridden by WidgetConfig
            description: initialWidget?.description || "", // Overridden by WidgetConfig
            width: initialWidget?.width ?? null, // Overridden by WidgetConfig
            height: initialWidget?.height ?? null, // Overridden by WidgetConfig
            showLegend: initialWidget?.showLegend ?? true, // Overridden by WidgetConfig
            showValues: initialWidget?.showValues ?? true, // Overridden by WidgetConfig
            showPercentage: initialWidget?.showPercentage ?? false, // Overridden by WidgetConfig
            hideYAxis: true, // Overridden by WidgetConfig
            data: new PieChartData(valuePathKey, valueAggregation, categoryPathKey, categoryAggregation, categorySortType, filter)
        }
        return newWidget;
    }

    function onFieldChange(objectKey: string | undefined, fieldKey: string | undefined) {
        if (!objectKey || !fieldKey) {
            setSelectedField(undefined);
            setSelectedObject(undefined);
            setSelectedFieldAggregation(undefined);
            setSelectedCategoryPath(undefined);
            setSelectedCategoryName(undefined);
            setSelectedCategoryAggregation(undefined);
            setSelectedCategorySortType(NONE);
            updateFieldAggregationByPathKeys(undefined, undefined);
            onConfigChange(undefined);
        } else {
            const field = fieldByKeys?.get(fieldKey);
            const fieldAggregation = getDefaultAggregation(field);
            const newWidget = getNewWidget(field, fieldAggregation, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedField(field);
            let defaultAggr = undefined;
            const validAggrs = validNumericAggregationByType(field?.type, field?.typeItems);
            if (validAggrs.length > 0) {
                defaultAggr = validAggrs[0];
            }
            setSelectedFieldAggregation(defaultAggr);
            updateFieldAggregationByPathKeys(defaultAggr, selectedCategoryAggregation);
            setSelectedObject(objectByKeys?.get(objectKey));
        }
    }

    function onCategoryChange(categoryId: string | undefined) {

        if (!objectByKeys || !relationByKeys) {
            setSelectedCategoryPath(undefined);
            setSelectedCategoryName(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, undefined);
            onConfigChange(undefined);
            return;
        }

        const treePathValue = getTreePathValue(categoryId, objectByKeys, relationByKeys);
        if (treePathValue) {
            const categoryField = fieldByKeys?.get(treePathValue.path.field);
            const categoryAggregation = getDefaultAggregation(categoryField);
            const newWidget = getNewWidget(selectedField, selectedFieldAggregation, treePathValue.path, categoryAggregation, selectedCategorySortType, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedCategoryAggregation(categoryAggregation);
            setSelectedCategoryPath(treePathValue.path);
            setSelectedCategoryName(treePathValue.name);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, categoryAggregation);
        } else {
            setSelectedCategoryPath(undefined);
            setSelectedCategoryName(undefined);
            setSelectedCategoryAggregation(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, undefined);
            onConfigChange(undefined);
        }
    }

    const onFieldAggregationChange = (value: string | undefined) => {
        if (value) {
            const fieldAggregation = value as AggregationType;
            const newWidget = getNewWidget(selectedField, fieldAggregation, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedFieldAggregation(fieldAggregation);
            updateFieldAggregationByPathKeys(fieldAggregation, selectedCategoryAggregation);
        } else {
            setSelectedFieldAggregation(undefined);
            updateFieldAggregationByPathKeys(undefined, selectedCategoryAggregation);
            onConfigChange(undefined);
        }
    }

    const onCategoryAggregationChange = (value: string | undefined) => {
        const categoryAggregation = value ? value as AggregationType : undefined;
        if (categoryAggregation) {
            const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedCategoryPath, categoryAggregation, selectedCategorySortType, selectedFilter);
            if (newWidget) {
                onConfigChange(newWidget);
            }
            setSelectedCategoryAggregation(categoryAggregation);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, categoryAggregation);
        } else {
            setSelectedCategoryAggregation(undefined);
            updateFieldAggregationByPathKeys(selectedFieldAggregation, undefined);
            onConfigChange(undefined);
        }
    }

    const onCategorySortTypeChange = (value: string | undefined) => {
        const sortType = value ? value as SortType : NONE;
        setSelectedCategorySortType(sortType);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedCategoryPath, selectedCategoryAggregation, sortType, selectedFilter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    const valueFieldTree = buildFieldTree(objectInfos, onFieldChange);
    const sharedObjectTree: TreeDataItem[] = sharedObjectInfos?.map(ws => ({
        id: ws.wsKey,
        name: workspaceByKeys?.get(ws.wsKey)?.name ?? "",
        children: ws.objectInfos.map(obj => ({
            id: obj.object._key,
            name: obj.object.name,
            children: obj.fields?.map(field => ({
                id: field._key,
                name: field.name,
                onClick: () => onFieldChange(obj.object._key, field._key)
            }))
        })),
    }));
    const aggregationFieldOptions = validNumericAggregationByType(selectedField?.type, selectedField?.typeItems);

    const categoryField = selectedCategoryPath?.field ? fieldByKeys?.get(selectedCategoryPath.field) : undefined;
    const aggregationCategoryOptions = validAggregationByType(categoryField?.type, categoryField?.typeItems);

    var objectTree: TreeDataItem | null = null;
    if (selectedObject) {
        objectTree = buildPathTree(objectInfos, sharedObjectInfos, selectedObject.object._key, onCategoryChange);
    }

    const separatorStyle = {
        backgroundColor: wsColor,
        opacity: 0.6
    }

    function onFilterChange(filter: Filter | null) {
        setSelectedFilter(filter);
        const newWidget = getNewWidget(selectedField, selectedFieldAggregation, selectedCategoryPath, selectedCategoryAggregation, selectedCategorySortType, filter);
        if (newWidget) {
            onConfigChange(newWidget);
        }
    }

    return (
        <>
            <Label className={TW_WidgetSectionLabel}>{t("dashboard.configuration.component.label")}</Label>
            <div className={TW_WidgetSpecificSection}>
                <Label>{t("dashboard.value.field.label")}</Label>
                {!selectedField && valueFieldTree && valueFieldTree.length > 0 && (
                    <TreeView searchable data={valueFieldTree} className={TW_DashboardCardTreeView} />
                )}
                {!selectedField && (!valueFieldTree || valueFieldTree.length === 0) && (
                    <span className={TW_DashboardCardFieldText}>{t("dashboard.value.field.no.objects")}</span>
                )}
                {!selectedField && sharedObjectTree && sharedObjectTree.length > 0 && (
                    <Collapsible open={isSharedObjectsOpen} onOpenChange={setIsSharedObjectsOpen} className="w-full">
                        <CollapsibleTrigger className={TW_SharedObjectsCollapsibleTrigger}>
                            <p className={TW_DashboardSection_Label}>{t(`dashboard.value.field.shared.objects.label`)}</p>
                            {isSharedObjectsOpen ? <ChevronDown className={TW_DashboardSection_Arrow} /> : <ChevronRight className={TW_DashboardSection_Arrow} />}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <ScrollArea className={TW_DashboardSection_ScrollArea}>
                                <TreeView searchable data={sharedObjectTree} className={TW_DashboardCardTreeView} />
                            </ScrollArea>
                        </CollapsibleContent>
                    </Collapsible>
                )}
                {selectedField && selectedObject &&
                    <Command className={TW_DashboardPathSelector}>
                        <CommandItem
                            className={TW_DashboardCardFieldSelected}
                            onSelect={() => onFieldChange(undefined, undefined)}>
                            <span className={TW_DashboardCardFieldText}>{selectedObject?.object.name + " > " + selectedField.name}</span>
                            <ChevronDown className={TW_DashboardCardFieldArrow} />
                        </CommandItem>
                    </Command>
                }
                {selectedField && aggregationFieldOptions.length > 0 && (
                    <>
                        <Label>{t("dashboard.value.aggregation.label")}</Label>
                        <Select value={selectedFieldAggregation?.toString()} onValueChange={onFieldAggregationChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.value.aggregation.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {aggregationFieldOptions.map(option => (
                                    <SelectItem key={option.toString()} value={option.toString()}>{t(`field.aggregation.label.${option}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                )}
                {selectedField && objectTree &&
                    <>
                        <Separator orientation="horizontal" className="w-3/4 my-4 mx-auto" style={separatorStyle} />
                        <Label>{t("dashboard.category.field.label")}</Label>
                        {!selectedCategoryPath && <TreeView searchable data={objectTree} className={TW_DashboardCardTreeView} />}
                        {selectedCategoryPath &&
                            <Command className={TW_DashboardPathSelector}>
                                <CommandItem
                                    className={TW_DashboardCardFieldSelected}
                                    onSelect={() => onCategoryChange(undefined)}>
                                    <span className={TW_DashboardCardFieldText}>{selectedCategoryName}</span>
                                    <ChevronDown className={TW_DashboardCardFieldArrow} />
                                </CommandItem>
                            </Command>
                        }
                    </>
                }
                {selectedCategoryPath && aggregationCategoryOptions.length > 0 && (
                    <>
                        <Label>{t("dashboard.category.aggregation.label")}</Label>
                        <Select value={selectedCategoryAggregation?.toString()} onValueChange={onCategoryAggregationChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.category.aggregation.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {aggregationCategoryOptions.map(option => (
                                    <SelectItem key={option.toString()} value={option.toString()}>{t(`field.aggregation.label.${option}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                )}
                {selectedCategoryPath && (
                    <>
                        <Label>{t("dashboard.category.sort.label")}</Label>
                        <Select value={selectedCategorySortType?.toString()} onValueChange={onCategorySortTypeChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.category.sort.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem key={NONE.toString()} value={NONE.toString()}>{t("sorting.type.name.NONE")}</SelectItem>
                                <SelectItem key={ASC.toString()} value={ASC.toString()}>{t("sorting.type.name.ASC")}</SelectItem>
                                <SelectItem key={DESC.toString()} value={DESC.toString()}>{t("sorting.type.name.DESC")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </>
                )}
                {selectedField && selectedObject &&
                    <div className={TW_WidgetSpecificSection}>
                        <Label className={TW_WidgetSectionLabel}>{t("dashboard.configuration.filter.label")}</Label>
                        <WidgetFilterEditor widget={initialWidget} srcObjectKey={selectedObject.object._key} onFilterChange={onFilterChange} fieldAggregationByPathKeys={fieldAggregationByPathKeys} />
                    </div>
                }
            </div>
        </>
    );
}